/**
 * ============================================================
 * lib/sentry.ts — Intégration Sentry pour catho-schoolcom
 * ============================================================
 *
 * Init no-op si VITE_SENTRY_DSN est absent (dev local sans monitoring).
 * Aucun appel ne bloquera si Sentry n'est pas configuré.
 *
 * Pattern aligné sur apps/cathoplus/lib/sentry.ts.
 */

import * as Sentry from '@sentry/react';

declare global {
  interface Window {
    __APP_CONFIG__?: { sentryDsn?: string; environment?: string };
  }
}

// Config runtime injectée par le BFF (/runtime-config.js) → 1 image unique
// promue sur 3 envs avec un `environment` distinct par env. Fallback VITE_*
// (build-time) pour le dev local.
const appCfg = (typeof window !== 'undefined' && window.__APP_CONFIG__) || {};
const dsn = appCfg.sentryDsn || (import.meta.env.VITE_SENTRY_DSN as string | undefined);
const environment = appCfg.environment
  ?? (import.meta.env.VITE_SENTRY_ENV as string | undefined)
  ?? (import.meta.env.DEV ? 'development' : 'production');
const release = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;

let initialized = false;

/**
 * Initialise Sentry. À appeler une seule fois au boot (main.tsx).
 * No-op si DSN absent — permet de tourner sans Sentry en dev.
 */
export function initSentry(): void {
  if (initialized) return;
  if (!dsn) {
    if (import.meta.env.DEV) console.log('[Sentry] DSN absent, monitoring désactivé');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance : 20% prod / 100% dev
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.2,
    // Session replay : 10% normal + 100% sur erreurs
    replaysSessionSampleRate: import.meta.env.DEV ? 0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Sanitize : retire les headers sensibles avant envoi
    beforeSend(event) {
      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.authorization;
        delete event.request.headers.Cookie;
        delete event.request.headers.cookie;
      }
      return event;
    },
  });

  initialized = true;
}

/**
 * Capture une exception manuellement (hors ErrorBoundary).
 */
export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    Sentry.captureException(error, context ? { extra: context } : undefined);
  } catch {
    // On ne laisse jamais Sentry casser l'app
  }
}

/**
 * Ajoute une trace (breadcrumb) pour contextualiser les erreurs futures.
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    Sentry.addBreadcrumb({ message, category, data, level: 'info' });
  } catch {
    // noop
  }
}

/**
 * Associe l'utilisateur courant à la session Sentry.
 * Passer null pour déconnecter (équivalent à clearUser).
 *
 * Signature volontairement permissive : on accepte n'importe quel claim
 * additionnel (paroisseId, role, direction…) → tags Sentry pour faciliter
 * le filtrage dans le dashboard.
 */
export function setUser(
  user:
    | {
        id: string;
        email?: string;
        username?: string;
        role?: string;
        roles?: string[];
        paroisseId?: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [k: string]: any;
      }
    | null,
): void {
  if (!initialized) return;
  try {
    if (user === null) {
      Sentry.setUser(null);
      Sentry.setTag('paroisse', '');
      Sentry.setTag('role', '');
      return;
    }
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });
    if (user.paroisseId) Sentry.setTag('paroisse', user.paroisseId);
    const roleTag = user.roles?.join(",") ?? user.role;
    if (roleTag) Sentry.setTag('role', roleTag);
  } catch {
    // noop
  }
}

/**
 * Déconnecte l'utilisateur courant côté Sentry (à appeler au logout).
 * Équivalent à `setUser(null)`, exposé séparément pour lisibilité.
 */
export function clearUser(): void {
  setUser(null);
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
